/**
 * Linking a finding to a component (a master tag of the facility): the search request the picker sends, the
 * shape it keeps per component, the "recent" strip and the group-by-component slicing of the findings table.
 *
 * Pure so the request rules are testable on their own. Two of them are easy to get silently wrong: the API
 * compares the search text against a lower-cased name without lowering the query (BaseTagController
 * GetBaseTags, verified 2026-09-07), and the master-only endpoint lists only harvested masters (7 of 263 in
 * the Merchant facility), so the picker reads `api/baseTags/typeHead`, which lists every tag of the facility.
 */
export const COMPONENT_TYPES = Object.freeze([
  Object.freeze({ key: 'Equipment', label: 'Equipment', icon: 'mdi-cog-outline' }),
  Object.freeze({ key: 'Pipeline', label: 'Pipeline', icon: 'mdi-pipe' }),
  Object.freeze({ key: 'Structural', label: 'Structural', icon: 'mdi-domain' }),
]);
export const COMPONENT_TYPE_KEYS = Object.freeze(COMPONENT_TYPES.map((t) => t.key));
export const COMPONENT_PAGE_SIZE = 50;
export const RECENT_COMPONENTS_MAX = 5;
export const UNLINKED_GROUP_KEY = '__not-linked';
export const UNLINKED_GROUP_NAME = 'Not linked';

const GENERIC_ICON = 'mdi-tag-outline';
const DISCRIMINATOR_PREFIX = /^Forge/;

/** 'ForgePipeline' -> 'Pipeline'; an already plain 'Pipeline' passes through; unknown values give null. */
export function componentTypeOf(value) {
  const plain = String(value || '').replace(DISCRIMINATOR_PREFIX, '');
  return COMPONENT_TYPE_KEYS.includes(plain) ? plain : null;
}

export function componentTypeIcon(type) {
  const found = COMPONENT_TYPES.find((t) => t.key === componentTypeOf(type));
  return found ? found.icon : GENERIC_ICON;
}

/**
 * Query for `GET api/baseTags/typeHead`. `search` is lower-cased because the endpoint lowers the name and not the
 * query; `sortBy` is set because the endpoint's default is creation date, which is meaningless to a picker.
 */
export function buildComponentQuery({ facilityId, search = '', type = '', page = 1 } = {}) {
  const term = String(search || '').trim().toLowerCase();
  return {
    assetId: facilityId,
    tagTypes: type ? [type] : [...COMPONENT_TYPE_KEYS],
    search: term || undefined,
    page: Math.max(1, Number(page) || 1),
    itemsPerPage: COMPONENT_PAGE_SIZE,
    sortBy: 'Name',
    sortDesc: false,
  };
}

/** The picker's own shape for one tag: { id, name, type }. `type` is null when the VM carries no known discriminator. */
export function toComponent(vm) {
  if (!vm || !vm.id) return null;
  return { id: String(vm.id), name: String(vm.name || vm.id), type: componentTypeOf(vm.type || vm.discriminator) };
}

/** `{ items, total }` from the endpoint's Page<T> (`{ items, total }`) or a bare array. */
export function componentPageOf(data) {
  const raw = data && Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
  const items = raw.map(toComponent).filter(Boolean);
  const total = data && Number.isFinite(Number(data.total)) ? Number(data.total) : items.length;
  return { items, total };
}

/** The component a finding is linked to, or null. Reads the VM fields the API returns (masterTagId / masterTagName / masterTagType). */
export function componentOfFinding(finding) {
  if (!finding || !finding.masterTagId) return null;
  return { id: String(finding.masterTagId), name: finding.masterTagName || finding.masterTagId, type: componentTypeOf(finding.masterTagType) };
}

/** The fields PATCH_FINDING sets after a link (or an unlink, when `component` is null). */
export function componentPatch(component) {
  if (!component) return { masterTagId: null, masterTagName: null, masterTagType: null };
  return { masterTagId: component.id, masterTagName: component.name, masterTagType: component.type || null };
}

/** What to tell the user when PUT AnomalyInsights/{id}/component fails. */
export function componentLinkErrorMessage(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  if (status === 409 && code === 'ANOMALY_INSIGHT_NOT_ACCEPTED') return 'This anomaly is no longer accepted. Refresh before changing its component.';
  if (status === 409) return 'The component changed or is no longer available. Refresh and try again.';
  if (status === 403) return 'You do not have permission to change this component link.';
  if (status === 404) return 'The anomaly or component is no longer available in this facility.';
  if (status === 400) return 'This tag type cannot be linked as a component.';
  return 'Could not update the component link. Please try again.';
}

/** New "recent" list with `component` first and no duplicates, capped. */
export function pushRecent(recent, component, max = RECENT_COMPONENTS_MAX) {
  if (!component || !component.id) return [...(recent || [])];
  return [component, ...(recent || []).filter((c) => c && c.id !== component.id)].slice(0, max);
}

/**
 * Findings sliced by linked component, in name order, the unlinked group last. `findings` should already be in
 * the order the table shows, so the sort applies inside each group.
 */
export function groupByComponent(findings) {
  const groups = new Map();
  (findings || []).forEach((finding) => {
    const component = componentOfFinding(finding);
    const key = component ? component.id : UNLINKED_GROUP_KEY;
    if (!groups.has(key)) groups.set(key, { key, name: component ? component.name : UNLINKED_GROUP_NAME, type: component ? component.type : null, rows: [] });
    groups.get(key).rows.push(finding);
  });
  const linked = [...groups.values()].filter((g) => g.key !== UNLINKED_GROUP_KEY).sort((a, b) => a.name.localeCompare(b.name));
  const unlinked = groups.get(UNLINKED_GROUP_KEY);
  return unlinked ? [...linked, unlinked] : linked;
}

/** "3 findings selected · 1 already linked" style counts for the bulk bar and the picker header. */
export function linkedCount(findings) {
  return (findings || []).filter((f) => f && f.masterTagId).length;
}
